import type { BaseColumn } from './ChannelList.types';

export enum Column {
  CHANNEL = 'channel',
  RATE_SOURCE = 'rateSource',
  PRODUCT = 'product',
  PO_NUMBER = 'poNumber',
  TARGETING_KPI = 'kpi',
  TARGETING_DETAILS = 'details',
  FORMAT = 'format',
  COUNTRY = 'country',
  CURRENCY = 'currency',
  BUYING_METRIC = 'buyingMetric',
  DSP = 'dsp',
  DSP_FEES = 'dspFees',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  SELL_RATE = 'sellRate',
  BUDGET = 'budget',
  NET_BUDGET = 'netBudget',
  VALUE = 'value',
  HARD_COST = 'hardCost',
  GROSS_MARGIN = 'grossMargin',
  TRADING_DEAL_PERCENT = 'tradingDealPercent',
  EXPECTED_MARGIN = 'expectedMargin',
  IMPRESSIONS = 'impressions',
  ESTIMATED_CLICKS = 'estimatedClicks',
  ESTIMATED_VIEWS = 'estimatedViews',
  ESTIMATED_LISTENS = 'estimatedListens',
  ACTIONS = 'actions'
}

export const COLUMNS: Record<Column, BaseColumn> = {
  [Column.CHANNEL]: {
    i18nKey: 'channel',
    dataIndex: 'channel',
    key: 'channel',
    align: 'center',
    width: 110
  },
  [Column.RATE_SOURCE]: {
    i18nKey: 'rateSource',
    dataIndex: 'rateSource',
    key: 'rateSource',
    align: 'center',
    width: 100
  },
  /* A PO is issued per line item — a client can raise one per channel, and
     often sends them weeks after signing — so it belongs beside the line it
     pays for rather than only on the plan. */
  [Column.PO_NUMBER]: {
    i18nKey: 'poNumber',
    dataIndex: 'poNumber',
    key: 'poNumber',
    width: 140,
    ellipsis: true,
    align: 'center'
  },
  [Column.PRODUCT]: {
    i18nKey: 'product',
    dataIndex: 'product',
    key: 'product',
    width: 180,
    ellipsis: true,
    align: 'center'
  },
  [Column.TARGETING_KPI]: {
    ellipsis: true,
    i18nKey: 'targetingKPI',
    dataIndex: 'kpi',
    key: 'kpi',
    align: 'center',
    width: 100
  },
  [Column.TARGETING_DETAILS]: {
    i18nKey: 'targetingDetails',
    dataIndex: 'targetingDetails',
    ellipsis: true,
    key: 'targetingDetails',
    className: 'targeting-details',
    align: 'center',
    width: 140
  },
  [Column.FORMAT]: {
    /* No ellipsis: it sets nowrap on the cell, which stops a long format tag
       wrapping and pushes it over the neighbouring columns. */
    i18nKey: 'format',
    dataIndex: 'format',
    key: 'format',
    align: 'center',
    width: 120
  },
  [Column.COUNTRY]: {
    ellipsis: true,
    i18nKey: 'country',
    dataIndex: 'country',
    key: 'country',
    align: 'center',
    width: 72
  },
  [Column.CURRENCY]: {
    ellipsis: true,
    i18nKey: 'currency',
    dataIndex: 'currency',
    key: 'currency',
    align: 'center',
    width: 72
  },
  [Column.BUYING_METRIC]: {
    ellipsis: true,
    i18nKey: 'buyingMetric',
    dataIndex: 'buyingMetric',
    key: 'buyingMetric',
    align: 'center',
    width: 100
  },
  [Column.DSP]: {
    ellipsis: true,
    i18nKey: 'dsp',
    dataIndex: 'dsp',
    key: 'dsp',
    align: 'center',
    width: 100
  },
  [Column.DSP_FEES]: {
    ellipsis: true,
    i18nKey: 'dspFees',
    dataIndex: 'dspFees',
    key: 'dspFees',
    width: 88,
    align: 'center'
  },
  [Column.START_DATE]: {
    ellipsis: true,
    i18nKey: 'startDate',
    dataIndex: 'startDate',
    key: 'startDate',
    align: 'center',
    width: 100
  },
  [Column.END_DATE]: {
    ellipsis: true,
    i18nKey: 'endDate',
    dataIndex: 'endDate',
    key: 'endDate',
    align: 'center',
    width: 100
  },
  [Column.SELL_RATE]: {
    ellipsis: true,
    i18nKey: 'sellRate',
    dataIndex: 'sellRate',
    key: 'sellRate',
    className: 'sell-rate',
    align: 'center',
    width: 100
  },
  [Column.BUDGET]: {
    ellipsis: true,
    i18nKey: 'grossBudget',
    dataIndex: 'budget',
    key: 'budget',
    className: 'budget',
    align: 'center',
    width: 110
  },
  [Column.NET_BUDGET]: {
    ellipsis: true,
    i18nKey: 'netBudget',
    dataIndex: 'netBudget',
    key: 'netBudget',
    className: 'net-budget',
    align: 'center',
    width: 110
  },
  [Column.VALUE]: {
    ellipsis: true,
    i18nKey: 'value',
    dataIndex: 'valueAddAbsolute',
    key: 'value',
    className: 'value-add',
    align: 'center',
    width: 100
  },
  [Column.HARD_COST]: {
    ellipsis: true,
    i18nKey: 'hardCost',
    dataIndex: 'hardCost',
    key: 'hardCost',
    className: 'hard-cost',
    align: 'center',
    width: 100
  },
  [Column.GROSS_MARGIN]: {
    ellipsis: true,
    i18nKey: 'grossMarginPercent',
    dataIndex: 'grossMarginPercent',
    key: 'grossMargin',
    width: 100,
    align: 'center',
    className: 'gross-margin-percent'
  },
  [Column.TRADING_DEAL_PERCENT]: {
    ellipsis: true,
    i18nKey: 'tradingDealPercent',
    dataIndex: 'tradingDealPercent',
    key: 'tradingDealPercent',
    className: 'trading-deal-percent',
    align: 'center',
    width: 80
  },
  [Column.EXPECTED_MARGIN]: {
    ellipsis: true,
    i18nKey: 'expectedMargin',
    dataIndex: 'marginPercent',
    key: 'expectedMargin',
    width: 88,
    align: 'center',
    className: 'gross-profit-percent'
  },
  [Column.IMPRESSIONS]: {
    ellipsis: true,
    i18nKey: 'impressions',
    dataIndex: 'impressions',
    key: 'impressions',
    align: 'center',
    width: 100
  },
  [Column.ESTIMATED_CLICKS]: {
    ellipsis: true,
    i18nKey: 'estimatedClicks',
    dataIndex: 'estimatedClicks',
    key: 'estimatedClicks',
    align: 'center',
    width: 100
  },
  [Column.ESTIMATED_VIEWS]: {
    ellipsis: true,
    i18nKey: 'estimatedViews',
    dataIndex: 'estimatedViews',
    key: 'estimatedViews',
    align: 'center',
    width: 100
  },
  [Column.ESTIMATED_LISTENS]: {
    ellipsis: true,
    i18nKey: 'estimatedListens',
    dataIndex: 'estimatedListens',
    key: 'estimatedListens',
    align: 'center',
    width: 100
  },
  [Column.ACTIONS]: {
    i18nKey: 'actions',
    dataIndex: 'actions',
    key: 'actions',
    fixed: 'right',
    className: 'actions',
    align: 'center',
    width: 130
  }
};

